import { useCallback, useEffect, useState } from 'react';
import api from '../../API';
import PrincipalAccessEditor, { ACCESS_LEVELS } from '../../components/PrincipalAccessEditor';
import styles from './CreateFolderModal.module.css';

/**
 * Resolve user or group id to display name via Girder API.
 */
async function resolvePrincipal(type, id) {
    const path = type === 'user' ? 'user' : 'group';
    try {
        const r = await api.get(`${path}/${id}`, {});
        if (r.status === 200 && r.data) {
            return type === 'user' ? (r.data.login || r.data.name || id) : (r.data.name || id);
        }
    } catch (_) {
        // ignore
    }
    return id;
}

/**
 * Modal to view and edit folder access (users and groups with read/write/admin).
 * Uses PrincipalAccessEditor (UserPicker + GroupPicker) so only valid users/groups can be added.
 * Only users with admin on the folder can save changes.
 */
function FolderAccessModal({ isOpen, onClose, folderId, folderName, onSaved }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [displayNames, setDisplayNames] = useState({ users: {}, groups: {} });

    const load = useCallback(() => {
        if (!folderId || !isOpen) return;
        setLoading(true);
        setError(null);
        api.get(`projects/folders/${folderId}/access`, {})
            .then((r) => {
                if (r.status === 200) {
                    setData(r.data);
                } else {
                    setError(r.data?.message || 'Failed to load access.');
                }
            })
            .catch((e) => setError(e.message || 'Failed to load access.'))
            .finally(() => setLoading(false));
    }, [folderId, isOpen]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!data?.access) {
            setDisplayNames({ users: {}, groups: {} });
            return;
        }
        const users = data.access.users || [];
        const groups = data.access.groups || [];
        const userPromises = users.map((u) => resolvePrincipal('user', u.id));
        const groupPromises = groups.map((g) => resolvePrincipal('group', g.id));
        Promise.all([...userPromises, ...groupPromises]).then((names) => {
            const n = users.length;
            const usersMap = {};
            users.forEach((u, i) => { usersMap[u.id] = names[i]; });
            const groupsMap = {};
            groups.forEach((g, i) => { groupsMap[g.id] = names[n + i]; });
            setDisplayNames({ users: usersMap, groups: groupsMap });
        });
    }, [data?.access]);

    function handleAccessChange(access) {
        if (!data) return;
        setData({ ...data, access });
    }

    function handleSave() {
        if (!data || data.myAccess < 2) return;
        setSaving(true);
        setError(null);
        api.put(`projects/folders/${folderId}/access`, { json: { access: data.access } })
            .then((r) => {
                if (r.status === 200) {
                    onSaved?.();
                    onClose();
                } else {
                    setError(r.data?.message || 'Failed to save.');
                }
            })
            .catch((e) => setError(e.message || 'Failed to save.'))
            .finally(() => setSaving(false));
    }

    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay} onClick={onClose} role="presentation">
            <div
                className={styles.modalContent}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-labelledby="folder-access-title"
            >
                <div className={styles.modalHeader}>
                    <h2 id="folder-access-title" className={styles.modalTitle}>
                        Access: {folderName || folderId}
                    </h2>
                    <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>
                <div className={styles.modalBody}>
                    {loading && <p>Loading…</p>}
                    {error && <div className={styles.errorMessage}>{error}</div>}
                    {data && !loading && (
                        <>
                            <div className={styles.fieldGroup}>
                                <span className={styles.fieldLabel}>Public</span>
                                <span>{data.public ? 'Yes' : 'No'}</span>
                            </div>
                            <PrincipalAccessEditor
                                value={data.access || { users: [], groups: [] }}
                                onChange={handleAccessChange}
                                displayNames={displayNames}
                                levels={ACCESS_LEVELS}
                                disabled={data.myAccess < 2}
                            />
                        </>
                    )}
                </div>
                {data && data.myAccess >= 2 && (
                    <div className={styles.modalFooter}>
                        <button type="button" className={styles.cancelButton} onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={styles.submitButton}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default FolderAccessModal;
